'use client';

import { useMemo, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { EditorState } from 'lexical';

import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';

import { ImageNode } from './nodes/ImageNode';
import { ColumnsNode } from './nodes/ColumnsNode';
import ToolbarPlugin from './plugins/ToolbarPlugin';


function isSerializedLexical(obj) {
    return obj && typeof obj === 'object' && obj.root && obj.root.type === 'root';
}

export default function LexicalEditor({
    initialJSON,
    onChange,
    readOnly = false,
    placeholder = 'Start typing…',
}) {
    // normalize initial state once
    const serializedInitial = useMemo(() => {
        if (typeof initialJSON === 'string') return initialJSON;
        if (isSerializedLexical(initialJSON)) return JSON.stringify(initialJSON);
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialJSON]);
    const [isToolbarDisabled, setIsToolbarDisabled] = useState(true);

    // dedupe change events
    const lastStr = useRef('');
    function handleChange(state) {
        const json = state.toJSON();
        const s = JSON.stringify(json);
        if (s === lastStr.current) return;
        lastStr.current = s;
        onChange(json);
    }

    const initialConfig = useMemo(
        () => ({
            namespace: 'page-editor',
            editable: !readOnly,
            theme: {
                paragraph: 'mb-2',
                heading: {
                    h1: 'text-3xl font-bold',
                    h2: 'text-2xl font-semibold',
                    h3: 'text-xl font-semibold',
                },
                list: { ul: 'list-disc pl-6', ol: 'list-decimal pl-6' },
                link: 'underline text-blue-600',
                quote: 'border-l-4 pl-4 italic text-slate-600',
            },
            onError(error) {
                console.error('[Lexical]', error);
            },
            nodes: [
                HeadingNode, QuoteNode,
                ListNode, ListItemNode,
                LinkNode,
                ImageNode,
                ColumnsNode,
            ],
            // initialize editor content here (no effects)
            editorState: serializedInitial,
        }),
        [readOnly, serializedInitial]
    );

    function handleFocus(event) {
        setIsToolbarDisabled(false);
    }

    function handleBlur(event) {
        // setIsToolbarDisabled(true);
    }

    return (
        <LexicalComposer initialConfig={initialConfig}>
            {!readOnly && <ToolbarPlugin initialState={serializedInitial} disabled={isToolbarDisabled} />} 
            <div className={`${readOnly ? 'border-0' : 'border'} rounded-xl bg-background p-3`}>
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className="min-h-[260px] outline-none"
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                    }
                    placeholder={<div className="pointer-events-none select-none text-slate-400">{placeholder}</div>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <OnChangePlugin onChange={handleChange} />
            </div>
        </LexicalComposer>
    );
}
